/**
 * The seven mounts
 *
 * Guru (Jupiter), Śani (Saturn), Sūrya (Sun), Budha (Mercury), Śukra (Venus),
 * Candra (Moon), Maṅgala (Mars — upper and lower). Positions follow the Sāmudrika tradition,
 * the classical hand tradition Pt I, and the classical text-independent palmistry practice.
 *
 * ⚠ **Prominence is a relative ranking, not a measurement.** Absolute mount height
 * cannot be recovered from one photograph. What a photograph does carry is
 * *shading*: a raised mount changes the low-frequency luminance across it more
 * than flat skin does. `prominence()` is the mean magnitude of that gradient in
 * the mount's region, divided by the palm's own mean, and `rankMounts()` orders
 * the mounts by it. The lighting direction is unknown, so even the ranking is
 * only as good as the light was even; every output says `basis: 'shading'`,
 * `absolute: false`.
 *
 * © Manish Jagdish Thatte
 */

import { fromPalmSpace } from './hand-geometry.js';
import { gaussianBlur } from './image.js';

export const MOUNT_GRAHA = Object.freeze({
  guru: 'Gu', shani: 'Sa', surya: 'Su', budha: 'Bu', shukra: 'Ve', chandra: 'Mo', mangala_upper: 'Ma', mangala_lower: 'Ma'
});

export const MOUNT_NAMES = Object.freeze({
  guru: { sanskrit: 'Guru', english: 'Jupiter' },
  shani: { sanskrit: 'Śani', english: 'Saturn' },
  surya: { sanskrit: 'Sūrya', english: 'Apollo (Sun)' },
  budha: { sanskrit: 'Budha', english: 'Mercury' },
  shukra: { sanskrit: 'Śukra', english: 'Venus' },
  chandra: { sanskrit: 'Candra', english: 'Luna (Moon)' },
  mangala_upper: { sanskrit: 'Maṅgala (upper)', english: 'Upper Mars' },
  mangala_lower: { sanskrit: 'Maṅgala (lower)', english: 'Lower Mars' }
});

/** Region centres and radii in palm space (u, v, r — r in palm widths). Engineering priors, unvalidated against real hands. */
export const MOUNT_LAYOUT = Object.freeze({
  guru: { u: 0.2, v: 0.86, r: 0.12 },
  shani: { u: 0.41, v: 0.88, r: 0.1 },
  surya: { u: 0.62, v: 0.87, r: 0.1 },
  budha: { u: 0.82, v: 0.84, r: 0.11 },
  shukra: { u: 0.17, v: 0.3, r: 0.22 },
  chandra: { u: 0.85, v: 0.26, r: 0.2 },
  mangala_upper: { u: 0.9, v: 0.55, r: 0.11 },
  mangala_lower: { u: 0.2, v: 0.62, r: 0.11 }
});

/** The mount regions as image-space discs: `{ id, cx, cy, r, u, v }`. */
export function mountRegions(frame) {
  return Object.entries(MOUNT_LAYOUT).map(([id, m]) => {
    const [cx, cy] = fromPalmSpace(m.u, m.v, frame);
    return { id, cx, cy, r: m.r * frame.width, u: m.u, v: m.v, graha: MOUNT_GRAHA[id] };
  });
}

function gradientMagnitude(gray) {
  const { width: w, height: h, data } = gray; const out = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
    const gx = (data[y * w + x + 1] - data[y * w + x - 1]) / 2; const gy = (data[(y + 1) * w + x] - data[(y - 1) * w + x]) / 2;
    out[y * w + x] = Math.hypot(gx, gy);
  }
  return out;
}

function meanIn(arr, w, h, region) {
  let s = 0; let n = 0;
  const x0 = Math.max(1, Math.floor(region.cx - region.r)); const x1 = Math.min(w - 2, Math.ceil(region.cx + region.r));
  const y0 = Math.max(1, Math.floor(region.cy - region.r)); const y1 = Math.min(h - 2, Math.ceil(region.cy + region.r));
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) if ((x - region.cx) ** 2 + (y - region.cy) ** 2 <= region.r * region.r) { s += arr[y * w + x]; n++; }
  return n ? s / n : 0;
}

/** Mean low-frequency shading gradient inside `region` (absolute units; compare only within one image). */
export function prominence(smoothGradient, width, height, region) {
  return meanIn(smoothGradient, width, height, region);
}

/**
 * Rank the mounts by relative shading prominence.
 *
 * Returns `[ { id, graha, names, score, relative, rank } ]`, most prominent first,
 * where `relative` is the mount's score divided by the mean over all mounts —
 * 1.0 is "typical for this palm". Also carries `basis` and `absolute` on the array.
 */
export function rankMounts(gray, frame) {
  const sigma = Math.max(2, frame.width / 14);
  const smooth = gaussianBlur(gray, sigma);
  const grad = gradientMagnitude(smooth);
  const regions = mountRegions(frame);
  const scores = regions.map((r) => ({ id: r.id, graha: r.graha, names: MOUNT_NAMES[r.id], score: prominence(grad, gray.width, gray.height, r) }));
  const mean = scores.reduce((a, s) => a + s.score, 0) / scores.length || 1;
  const out = scores.map((s) => ({ ...s, relative: s.score / mean })).sort((a, b) => b.score - a.score).map((s, i) => ({ ...s, rank: i + 1 }));
  out.basis = 'shading'; out.absolute = false;
  return out;
}
